import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  cancelarSyncFipe,
  dispararSyncFipe,
  FalhaFipeItem,
  fetchFalhasFipe,
  fetchJobDetalheFipe,
  fetchJobsFipe,
  fetchOverviewFipe,
  OPCOES_TIPO_FIPE,
  OverviewFipe,
  reprocessarFipe,
  ROTULO_ETAPA_FALHA,
  ROTULO_TIPO_FIPE,
  TITULO_TIPO_FIPE,
} from '../../../api/fipeSync/fipeSyncApi';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import ConfirmModal from '../../../components/modal/confirm/ConfirmModal';
import Paginacao from '../../../components/paginacao/Paginacao';
import BlocoCard from '../../../components/sync-painel/BlocoCard';
import HistoricoSection from '../../../components/sync-painel/HistoricoSection';
import JobDetalheModal from '../../../components/sync-painel/JobDetalheModal';
import TabelaSistema, { ColunaTabela } from '../../../components/sync-painel/TabelaSistema';
import { formatarDataHoraCurta } from '../../../components/sync-painel/statusBadge';
import { notify } from '../../../shared/feedback/notify';
import FalhaFipeModal from '../components/FalhaFipeModal';
// Esqueleto de página e cabeçalho compartilhados + vocabulário das telas de configuração.
// Import explícito de propósito: contar com o CSS que outra tela carrega é acoplamento
// invisível (lição do BUG-V74). Esta tela não tem CSS próprio.
import '../../recepcao/pages/Recepcao.css';
import '../../admin-sistema/pages/css/adminSistema.css';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/ui/badge-status.css';
import '../../../shared/feedback/aviso-de-tela.css';

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: '1. O que esta tela controla',
    conteudo: (
      <p>
        O sistema mantém uma <strong>cópia local da tabela FIPE</strong> (marcas, modelos, anos e preços de
        carros e motos), que é a fonte da seleção de veículo no cadastro da Recepção. Esta tela dispara e
        acompanha a atualização dessa cópia a partir da tabela FIPE oficial, mostra o que ficou pendente e
        permite reprocessar somente o que falhou.
      </p>
    ),
  },
  {
    titulo: '2. As quatro fases',
    conteudo: (
      <p>
        A sincronização percorre, nesta ordem: <strong>Marcas</strong>, <strong>Modelos</strong> (marca a
        marca), <strong>Anos</strong> (modelo a modelo) e <strong>Preços</strong> (combinação a combinação). O
        andamento aparece ao vivo no card — a fase, o item que está sendo processado e o progresso. A varredura
        completa leva em torno de dois dias, e <strong>só uma execução roda por vez</strong>: disparar com outra
        em andamento gera um aviso apontando a execução atual, em vez de criar uma segunda.
      </p>
    ),
  },
  {
    titulo: '3. Falha parcial não é começar de novo',
    conteudo: (
      <p>
        Quando a tabela FIPE não responde por uma marca ou por um modelo, aquele item é pulado e vira uma linha
        em <strong>Pendências</strong>, com o motivo. Os dados antigos dele continuam disponíveis — ficam apenas
        desatualizados. <strong>Reprocessar</strong> refaz somente o que falhou, e em cascata: reprocessar uma
        marca refaz também os anos e os preços dela. Quando o reprocessamento zera tudo que faltava, a execução
        original muda de <strong>Falha parcial</strong> para <strong>Recuperado</strong>. Uma sincronização
        completa sem nenhuma pendência também torna obsoletas as pendências antigas.
      </p>
    ),
  },
  {
    titulo: '4. O que o reprocessamento nunca faz',
    conteudo: (
      <p>
        A fase de <strong>Marcas</strong> nunca é refeita isoladamente, por proteção ao dado: ela é a única que
        remove marcas que saíram da tabela FIPE, e refazê-la fora da sincronização completa poderia apagar
        informação válida caso a FIPE respondesse uma lista parcial. Se a fase de Marcas falhar, a execução
        inteira falha e o caminho é <strong>disparar a sincronização completa</strong> de novo.
      </p>
    ),
  },
  {
    titulo: '5. Cancelar',
    conteudo: (
      <p>
        O cancelamento é registrado e a execução para <strong>em alguns segundos</strong>, valendo mesmo que
        ela esteja sendo processada em outro servidor. O que já foi gravado <strong>permanece</strong>; o
        restante fica como estava até a próxima execução. A execução cancelada aparece no histórico como{' '}
        <strong>Cancelado</strong>, com as fases que chegou a concluir.
      </p>
    ),
  },
  {
    titulo: '6. Interrupções e retomada automática',
    conteudo: (
      <>
        <p>
          A varredura leva cerca de dois dias e roda numa execução separada do sistema, que continua mesmo
          durante uma atualização. Se ela for interrompida assim mesmo, a execução{' '}
          <strong>não é perdida</strong>: ela guarda o ponto exato onde estava e é retomada automaticamente
          dali na verificação diária seguinte, às 08h. As fases já concluídas não são refeitas.
        </p>
        <p>
          Enquanto está trabalhando, a execução dá um <strong>sinal de vida</strong> a cada poucos segundos. Se
          esse sinal parar por mais de cinco minutos, o card avisa — é atenção, não erro:{' '}
          <strong>não cancele nem dispare outra</strong>, porque a retomada acontece sozinha e recomeçar
          descartaria dias de trabalho. Só acione a TI se o aviso continuar depois da verificação das 08h.
        </p>
        <p>
          Uma execução que precise ser retomada muitas vezes seguidas (dez) indica um problema que não se
          resolve sozinho: aí ela é encerrada como <strong>Abandonado</strong> e um e-mail é enviado. Nesse
          caso, o caminho é disparar uma nova sincronização.
        </p>
      </>
    ),
  },
  {
    titulo: '7. O que roda sozinho',
    conteudo: (
      <p>
        Todo dia às 08h o servidor verifica se a última sincronização completa bem-sucedida tem{' '}
        <strong>15 dias ou mais</strong> e, se tiver, dispara uma nova — ou seja, a cadência é{' '}
        <strong>quinzenal</strong>; a verificação é diária só para não perder a janela. A decisão vem da data da última
        execução, não de uma data fixa do calendário — então, se o serviço estiver fora do ar no horário, a
        sincronização acontece na verificação seguinte, em vez de ser perdida. Se um disparo manual coincidir com
        o automático, o sistema recusa com aviso apontando a execução em andamento.
      </p>
    ),
  },
  {
    titulo: '8. De onde vem o dado, e o quanto ele está fresco',
    conteudo: (
      <p>
        Os dados vêm da tabela FIPE pública. O <strong>mês de referência</strong> exibido no card{' '}
        <strong>Dados no sistema</strong> é o mês da tabela FIPE que está valendo na cópia local — é o indicador
        de quão atual está o preço que a Recepção vê. As contagens ao lado dele mostram o tamanho do que está
        disponível hoje.
      </p>
    ),
  },
];

/** 409 = já existe execução: aviso com o job apontado, nunca toast de erro. */
const tratarErroDisparo = (error: unknown, oQue: string) => {
  const resposta = (error as { response?: { status?: number; data?: { message?: string } } })?.response;
  if (resposta?.status === 409) {
    notify.warning(
      resposta.data?.message ?? `Já existe uma sincronização em execução — aguarde terminar para ${oQue}.`,
      { toastId: 'fipe-sync-conflito' },
    );
    return;
  }
  notify.errorFrom(error, `Não foi possível ${oQue}. Tente novamente; se persistir, acione a TI.`);
};

const numero = (v: number): string => v.toLocaleString('pt-BR');

/**
 * Nenhuma resposta HTTP chegou (axios reporta "Network Error"): serviço fora do ar,
 * inalcançável na rede, ou CORS bloqueando. É diferente de o serviço ter respondido com
 * erro, e o texto tem de dizer isso — a FIPE é um SERVIÇO SEPARADO, então "recarregue a
 * página" manda o usuário tentar consertar o que não está do lado dele. Mesmo princípio
 * da consulta à rastreadora: a mensagem diz de quem é o problema.
 */
const servicoNaoRespondeu = (error: unknown): boolean =>
  Boolean(error) && (error as { response?: unknown }).response === undefined;

const SEM_RESPOSTA_TEXTO =
  'O serviço da FIPE não respondeu. Ele pode estar fora do ar ou inacessível a partir desta rede — acione a TI. Isto não é falha do sistema da oficina.';

const ERRO_DE_CARGA_TEXTO =
  'Não foi possível carregar o estado da sincronização. As informações abaixo podem estar desatualizadas — recarregue a página; se persistir, acione a TI.';

/**
 * A partir de quantos segundos sem sinal de vida a tela avisa. É o MESMO limiar que o
 * backend usa para considerar uma execução órfã e reivindicá-la — avisar antes disso
 * mostraria alarme para o intervalo em que o silêncio ainda é normal (a batida de ponto é
 * a cada 5s, mas um item lento pode levar até o timeout da API da FIPE).
 */
const SEM_SINAL_LIMIAR_SEGUNDOS = 300;

/** Pendências por página. Mesmo tamanho do histórico de execuções, logo abaixo na tela. */
const PAGE_SIZE_PENDENCIAS = 20;

const SincronizacaoFipePage: React.FC = () => {
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [jobDetalheId, setJobDetalheId] = useState<string | null>(null);
  const [falhaSelecionada, setFalhaSelecionada] = useState<FalhaFipeItem | null>(null);
  const [confirmDisparo, setConfirmDisparo] = useState(false);
  const [confirmCancelamento, setConfirmCancelamento] = useState(false);
  const [confirmReprocessarTodas, setConfirmReprocessarTodas] = useState(false);

  const queryClient = useQueryClient();

  const {
    data: overview,
    error: overviewError,
    isError: overviewFalhou,
    isLoading,
  } = useQuery<OverviewFipe>({
    queryKey: ['fipe-sync', 'overview'],
    queryFn: fetchOverviewFipe,
    // Polling: 10s com execução em andamento (a etapa atual muda), 60s em repouso.
    refetchInterval: (query) => (query.state.data?.ultimoFull?.status === 'RUNNING' ? 10_000 : 60_000),
  });

  useEffect(() => {
    if (overviewFalhou) {
      /*
       * ⚠️ Sem resposta HTTP, o `errorFrom` devolve o `error.message` do axios cru — o
       * usuário lê "Network Error" e não sabe o que fazer (foi o que aconteceu no
       * primeiro teste real, 25/08/2026). Neste ramo o texto é FIXO, pela regra do
       * projeto: motivo HTTP que substitui informação útil não vai para a tela.
       */
      if (servicoNaoRespondeu(overviewError)) {
        notify.error(SEM_RESPOSTA_TEXTO, { toastId: 'fipe-sync-overview' });
      } else {
        notify.errorFrom(
          overviewError,
          'Não foi possível carregar o painel de sincronização da FIPE. Recarregue a página; se persistir, acione a TI.',
          { toastId: 'fipe-sync-overview' },
        );
      }
    }
  }, [overviewFalhou, overviewError]);

  /*
   * Pendências vêm PAGINADAS da rota própria, não do `overview` (07/09/2026).
   *
   * O `overview` devolve a lista inteira de pendências num campo só, e com o acúmulo real
   * (469 em 06/09/2026) a tela ficou pesada de carregar. A rota `GET /api/sync/falhas` já
   * era paginada e não tinha consumidor.
   */
  const [paginaPendencias, setPaginaPendencias] = useState(1);

  const {
    data: pendencias,
    error: pendenciasError,
    isError: pendenciasFalharam,
  } = useQuery({
    queryKey: ['fipe-sync', 'falhas', paginaPendencias],
    queryFn: () => fetchFalhasFipe({ pendentes: true, page: paginaPendencias, pageSize: PAGE_SIZE_PENDENCIAS }),
    refetchInterval: 60_000,
  });

  useEffect(() => {
    if (pendenciasFalharam) {
      notify.errorFrom(
        pendenciasError,
        'Não foi possível carregar as pendências de reprocessamento. Recarregue a página; se persistir, acione a TI.',
        { toastId: 'fipe-sync-falhas' },
      );
    }
  }, [pendenciasFalharam, pendenciasError]);

  const recarregar = () => {
    queryClient.invalidateQueries({ queryKey: ['fipe-sync'] });
  };

  const disparoMutation = useMutation({
    mutationFn: dispararSyncFipe,
    onSuccess: (r) => {
      notify.success(
        `Sincronização iniciada em segundo plano (execução ${r.jobId.slice(0, 8)}...). Acompanhe o progresso neste painel.`,
      );
      recarregar();
    },
    onError: (e) => tratarErroDisparo(e, 'disparar a sincronização'),
  });

  const cancelamentoMutation = useMutation({
    mutationFn: cancelarSyncFipe,
    onSuccess: (r) => {
      notify.success(r.message);
      recarregar();
    },
    onError: (e) => notify.errorFrom(e, 'Não foi possível solicitar o cancelamento. Tente novamente.'),
  });

  const reprocessarMutation = useMutation({
    mutationFn: (falhaIds?: string[]) => reprocessarFipe(falhaIds),
    onSuccess: (r) => {
      notify.success(
        `Reprocessamento iniciado em segundo plano (execução ${r.jobId.slice(0, 8)}...). Acompanhe o progresso neste painel.`,
      );
      setFalhaSelecionada(null);
      recarregar();
    },
    onError: (e) => tratarErroDisparo(e, 'iniciar o reprocessamento'),
  });

  const ultimoFull = overview?.ultimoFull ?? null;
  const rodando = ultimoFull?.status === 'RUNNING' || overview?.ultimoRecover?.status === 'RUNNING';

  /*
   * Execução em andamento que parou de dar sinal de vida. O limiar da tela (5 min) é o
   * mesmo do backend para reivindicar um job órfão: antes disso, silêncio é normal — a
   * batida de ponto acontece a cada 5 segundos, mas um item lento pode levar até o timeout
   * da API da FIPE. `semSinalHaSegundos` vem calculado do servidor de propósito.
   */
  const segundosSemSinal = ultimoFull?.status === 'RUNNING' ? ultimoFull.semSinalHaSegundos ?? null : null;
  const semSinal = segundosSemSinal !== null && segundosSemSinal > SEM_SINAL_LIMIAR_SEGUNDOS;
  const minutosSemSinal = segundosSemSinal !== null ? Math.floor(segundosSemSinal / 60) : 0;
  const acaoPendente = disparoMutation.isPending || cancelamentoMutation.isPending || reprocessarMutation.isPending;

  const colunasPendencias = useMemo<ColunaTabela<FalhaFipeItem>[]>(
    () => [
      {
        titulo: 'Fase',
        largura: 120,
        texto: (f) => ROTULO_ETAPA_FALHA[f.etapa] ?? f.etapa,
        render: (f) => <span className="cilia-badge badge-laranja">{ROTULO_ETAPA_FALHA[f.etapa] ?? f.etapa}</span>,
      },
      {
        titulo: 'O que ficou de fora',
        largura: 300,
        // Célula é uma linha só: o secundário vai ao lado, nunca embaixo (regra do projeto).
        texto: (f) =>
          [f.escopo?.marcaNome, f.escopo?.modeloNome, f.escopo?.anoNome].filter(Boolean).join(' > ') || '-',
      },
      { titulo: 'Motivo', largura: 320, texto: (f) => f.motivo },
      { titulo: 'Registrada em', largura: 150, texto: (f) => formatarDataHoraCurta(f.criadoEm) },
    ],
    [],
  );

  // O total vem da rota paginada (a mesma consulta que alimenta a tabela), com o do
  // `overview` como reserva enquanto a primeira página não chegou.
  const totalPendencias = pendencias?.total ?? overview?.falhasPendentes.total ?? 0;
  const totalPaginasPendencias = Math.max(1, Math.ceil(totalPendencias / PAGE_SIZE_PENDENCIAS));

  return (
    <div className="ConteDo recepcao-conte-do">
      <div className="recepcao-header-row">
        <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Sincronização FIPE
          <button type="button" className="help-circle-btn" title="Como Funciona" onClick={() => setIsHelpOpen(true)}>
            ?
          </button>
        </div>
      </div>

      <p className="admin-subtitle" style={{ margin: '0 0 24px 0' }}>
        Cópia local da tabela FIPE que alimenta o cadastro de veículos. Dispare, acompanhe e trate as
        sincronizações por aqui.
      </p>

      {overviewFalhou && (
        <div className="erro-de-tela" role="alert">
          {servicoNaoRespondeu(overviewError) ? SEM_RESPOSTA_TEXTO : ERRO_DE_CARGA_TEXTO}
        </div>
      )}

      {isLoading && <div className="admin-vazio">Carregando o estado da sincronização...</div>}

      {overview && (
        <>
          {/*
            Faixa de progresso ao vivo. Existe separada do card porque a linha é longa
            (fase + item + N/M) e no card ela é truncada com reticências — aqui aparece
            inteira, que é o dado que faltava antes desta tela existir.
          */}
          {ultimoFull?.status === 'RUNNING' && (
            <div
              className="admin-card"
              style={{ marginBottom: 24, padding: '16px 24px', borderLeft: '4px solid #EA5D0E' }}
            >
              <div className="admin-card-title" style={{ marginBottom: 6, fontSize: 15 }}>
                Em execução agora
              </div>
              <div style={{ fontSize: 15, color: 'var(--tema-texto, #1A1C1B)', wordBreak: 'break-word' }}>
                {ultimoFull.etapaAtual ?? 'iniciando...'}
              </div>
              <p className="admin-hint" style={{ marginTop: 8, marginBottom: 0 }}>
                Começou em {formatarDataHoraCurta(ultimoFull.startedAt)}. A varredura completa leva cerca de dois
                dias; esta linha se atualiza sozinha.
                {(ultimoFull.retomadas ?? 0) > 0 &&
                  ` Esta execução já foi retomada ${ultimoFull.retomadas} vez(es) depois de interrupções, continuando de onde parou.`}
              </p>

              {/*
                Aviso de execução sem sinal (fipe/at-04). É ATENÇÃO, não erro: o serviço
                retoma sozinho do ponto onde parou, e chamar isso de falha faria o
                administrador cancelar e recomeçar uma varredura de 48h sem necessidade.
                A conta vem do servidor — o relógio do navegador pode estar adiantado.
              */}
              {semSinal && (
                <p className="aviso-de-tela" style={{ marginTop: 12, marginBottom: 0 }} role="status">
                  Esta execução está sem sinal há {minutosSemSinal} minuto(s). O serviço retoma sozinho de onde
                  parou, sem perder o que já foi gravado. Se continuar assim por mais de 15 minutos, acione a TI.
                </p>
              )}
            </div>
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
              gap: 20,
              alignItems: 'stretch',
              marginBottom: 24,
            }}
          >
            <BlocoCard
              titulo="Sincronização completa"
              descricao="Percorre a tabela FIPE inteira: marcas, modelos, anos e preços de carros e motos. Leva cerca de dois dias e só uma execução roda por vez."
              job={ultimoFull}
              onVerDetalhe={setJobDetalheId}
              acoes={
                <>
                  <button
                    type="button"
                    className="admin-btn admin-btn-primary"
                    disabled={acaoPendente || ultimoFull?.status === 'RUNNING'}
                    onClick={() => setConfirmDisparo(true)}
                  >
                    Disparar sincronização (confirma antes)
                  </button>
                  {ultimoFull?.status === 'RUNNING' && (
                    <button
                      type="button"
                      className="admin-btn admin-btn-secondary"
                      disabled={acaoPendente}
                      onClick={() => setConfirmCancelamento(true)}
                    >
                      Cancelar execução
                    </button>
                  )}
                </>
              }
            />

            <div className="admin-card" style={{ display: 'flex', flexDirection: 'column', marginBottom: 0 }}>
              <div className="admin-card-title" style={{ marginBottom: 8 }}>
                Dados no sistema
              </div>
              <p className="admin-hint" style={{ margin: '0 0 14px 0' }}>
                O que a Recepção enxerga hoje no cadastro de veículos. O mês de referência é o da tabela FIPE que
                está valendo nesta cópia.
              </p>

              {[
                ['Mês de referência', overview.dados.referenceMonth ?? 'sem registro'],
                ['Marcas', numero(overview.dados.marcas)],
                ['Modelos', numero(overview.dados.modelos)],
                ['Anos', numero(overview.dados.anos)],
                ['Preços', numero(overview.dados.veiculos)],
              ].map(([rotulo, valor]) => (
                <div
                  className="admin-linha-item"
                  key={rotulo}
                  style={{ padding: '7px 0', gap: 12, justifyContent: 'flex-start' }}
                >
                  <span className="admin-texto-secundario" style={{ width: 140, flexShrink: 0 }}>
                    {rotulo}
                  </span>
                  <span className="admin-texto-secundario" style={{ color: 'var(--tema-texto, #1A1C1B)' }}>
                    {valor}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
            <div className="admin-card-title" style={{ marginBottom: 0 }}>
              Pendências de reprocessamento
            </div>
            {totalPendencias > 0 && (
              <button
                type="button"
                className="admin-btn admin-btn-primary"
                disabled={acaoPendente || rodando}
                onClick={() => setConfirmReprocessarTodas(true)}
              >
                Reprocessar todas ({totalPendencias})
              </button>
            )}
          </div>

          <p className="admin-hint" style={{ marginTop: 0, marginBottom: 12 }}>
            O que ficou de fora da última sincronização. Clique numa linha para ver o escopo e o{' '}
            <strong>motivo da falha</strong>; reprocessar refaz somente aquele item, em cascata — nunca a
            sincronização inteira.
          </p>

          <div
            style={{
              width: '100%',
              paddingTop: 30,
              background: 'var(--tema-superficie, white)',
              borderRadius: 16,
              border: '1px var(--tema-linha-2, #CACACA) solid',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              display: 'flex',
              marginBottom: 24,
            }}
          >
            <TabelaSistema
              colunas={colunasPendencias}
              itens={pendencias?.itens ?? []}
              chave={(f) => f.id}
              vazio={
                pendenciasFalharam
                  ? 'As pendências não pôderam ser carregadas — veja o aviso acima.'
                  : 'Nenhuma pendência: tudo que foi sincronizado está completo.'
              }
              onClickLinha={setFalhaSelecionada}
            />

            {totalPaginasPendencias > 1 && (
              <Paginacao
                paginaAtual={paginaPendencias}
                totalPaginas={totalPaginasPendencias}
                onMudarPagina={setPaginaPendencias}
              />
            )}
          </div>

          <div className="admin-card" style={{ marginBottom: 24 }}>
            <div className="admin-card-title" style={{ marginBottom: 8 }}>
              O que roda sozinho
            </div>
            <p className="admin-hint" style={{ margin: '0 0 10px 0' }}>
              Cron do próprio serviço. Evite disparar manualmente perto desse horário.
            </p>
            {overview.crons.map((c) => (
              <div
                className="admin-linha-item"
                key={c.quando + c.oque}
                style={{ padding: '7px 0', gap: 16, justifyContent: 'flex-start' }}
              >
                <span className="admin-texto-secundario" style={{ width: 240, flexShrink: 0 }}>
                  {c.quando}
                </span>
                <span className="admin-texto-secundario" style={{ color: 'var(--tema-texto, #1A1C1B)' }}>
                  {c.oque}
                </span>
              </div>
            ))}
          </div>

          <HistoricoSection
            onVerDetalhe={setJobDetalheId}
            algumRodando={Boolean(rodando)}
            fetchJobs={fetchJobsFipe}
            queryKeyBase="fipe-sync"
            rotulosTipo={ROTULO_TIPO_FIPE}
            opcoesTipo={OPCOES_TIPO_FIPE}
            tituloColunaTipo="Tipo"
          />
        </>
      )}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Sincronização FIPE"
        secoes={helpSections}
      />

      {jobDetalheId && (
        <JobDetalheModal
          jobId={jobDetalheId}
          onClose={() => setJobDetalheId(null)}
          fetchDetalhe={fetchJobDetalheFipe}
          queryKeyBase="fipe-sync"
          titulosPorTipo={TITULO_TIPO_FIPE}
        />
      )}

      {falhaSelecionada && (
        <FalhaFipeModal
          item={falhaSelecionada}
          onClose={() => setFalhaSelecionada(null)}
          onReprocessar={(f) => reprocessarMutation.mutate([f.id])}
          reprocessando={reprocessarMutation.isPending}
        />
      )}

      <ConfirmModal
        isOpen={confirmDisparo}
        title="Disparar sincronização completa"
        message="A sincronização vai percorrer a tabela FIPE inteira (marcas, modelos, anos e preços de carros e motos). Leva cerca de dois dias, e um e-mail é enviado ao término. Enquanto ela roda, nenhuma outra execução pode ser iniciada. Deseja continuar?"
        confirmLabel="Disparar"
        onConfirm={() => {
          setConfirmDisparo(false);
          disparoMutation.mutate();
        }}
        onCancel={() => setConfirmDisparo(false)}
      />

      <ConfirmModal
        isOpen={confirmCancelamento}
        title="Cancelar a execução em andamento"
        message="A execução será interrompida em alguns segundos, mesmo que esteja sendo processada em outro servidor. Tudo que já foi gravado permanece; o restante fica como estava até a próxima sincronização. Deseja cancelar?"
        confirmLabel="Cancelar execução"
        onConfirm={() => {
          setConfirmCancelamento(false);
          cancelamentoMutation.mutate();
        }}
        onCancel={() => setConfirmCancelamento(false)}
      />

      <ConfirmModal
        isOpen={confirmReprocessarTodas}
        title="Reprocessar todas as pendências"
        message={`Serão reprocessadas ${totalPendencias} pendência(s), em segundo plano e em cascata: recuperar uma marca refaz também os anos e os preços dela. A fase de Marcas não é refeita. Deseja continuar?`}
        confirmLabel="Reprocessar"
        onConfirm={() => {
          setConfirmReprocessarTodas(false);
          reprocessarMutation.mutate(undefined);
        }}
        onCancel={() => setConfirmReprocessarTodas(false)}
      />
    </div>
  );
};

export default SincronizacaoFipePage;
